// Secure Invites — operator onboarding surface.
//
// Powered: create + revoke/expire against the real sq_* invite tables
// via api.commandCenter.invites.*. There is intentionally NO global
// invite list endpoint yet — this surface NEVER fabricates a directory.
// It renders a create form and an in-session list of the invites you
// created here (returned CommandCenterInvite rows), with per-row Revoke
// and Expire actions that update the row status.
//
// Admin-gated (server minLevel 80). Fails closed: auth-required and
// forbidden render honest EmptyStates rather than an error banner.

import { useState } from 'react'
import {
  Button,
  Input,
  Select,
  FormField,
  DataGrid,
  Banner,
  InlineAlert,
} from '@square/ui'
import type { CommandCenterInvite } from '../../../api/client'
import { api, ApiError, isAuthError, isForbiddenError } from '../../../api/client'
import RouteHeader from '../../RouteHeader'
import EmptyState from '../../../components/EmptyState'
import { SHELL_TOKENS, chipStyle, text } from '../../../shell/styles'

type Scope = 'project' | 'org'
type ExpiresIn = '24h' | '7d' | '30d'

const SCOPE_OPTIONS: Array<{ value: Scope; label: string }> = [
  { value: 'project', label: 'Project' },
  { value: 'org', label: 'Organization' },
]

const EXPIRES_OPTIONS: Array<{ value: ExpiresIn; label: string }> = [
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
]

function formatStamp(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`
}

function statusAccent(status: string): string {
  switch (status) {
    case 'revoked':
    case 'expired':
      return SHELL_TOKENS.red
    case 'claimed':
    case 'accepted':
    case 'active':
      return SHELL_TOKENS.green
    case 'pending':
      return SHELL_TOKENS.gold
    default:
      return SHELL_TOKENS.inkFaint
  }
}

export default function Invites() {
  // Create-form fields
  const [scope, setScope] = useState<Scope>('project')
  const [email, setEmail] = useState('')
  const [projectId, setProjectId] = useState('')
  const [orgId, setOrgId] = useState('')
  const [role, setRole] = useState('')
  const [expiresIn, setExpiresIn] = useState<ExpiresIn>('7d')

  // In-session list of invites created here. There is NO global list
  // endpoint, so this only ever contains rows this session created.
  const [invites, setInvites] = useState<CommandCenterInvite[]>([])

  // Non-happy states.
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [forbidden, setForbidden] = useState(false)

  // Busy flags — creating, and per-row action busy by invite id.
  const [creating, setCreating] = useState(false)
  const [rowBusy, setRowBusy] = useState<string | null>(null)

  function formatError(err: unknown): string {
    if (err instanceof ApiError) {
      if (!err.message || err.message === err.code) return err.code
      return `${err.code}: ${err.message}`
    }
    if (err instanceof Error) return err.message || 'request_failed'
    return String(err)
  }

  // Central handler for the three non-happy states on any call.
  // Returns true if it consumed the error (caller should stop).
  function handleGuarded(err: unknown): boolean {
    if (isAuthError(err)) {
      setAuthRequired(true)
      setForbidden(false)
      setError(null)
      return true
    }
    if (isForbiddenError(err) || (err instanceof ApiError && err.code === 'forbidden')) {
      setForbidden(true)
      setAuthRequired(false)
      setError(null)
      return true
    }
    return false
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setError('Recipient email is required.')
      return
    }
    if (scope === 'project' && !projectId.trim()) {
      setError('Project ID is required for a project-scoped invite.')
      return
    }
    if (scope === 'org' && !orgId.trim()) {
      setError('Org ID is required for an org-scoped invite.')
      return
    }

    setCreating(true)
    try {
      const payload = {
        scope,
        email: trimmedEmail,
        role: role.trim() || undefined,
        expires_in: expiresIn,
        ...(scope === 'project'
          ? { project_id: projectId.trim() }
          : { org_id: orgId.trim() }),
      }
      const result = await api.commandCenter.invites.create(payload)
      // Push the honest returned row; never invent fields.
      setInvites((prev) => [result.invite, ...prev])
      // Reset the recipient so the operator can send the next one.
      setEmail('')
    } catch (err) {
      if (handleGuarded(err)) return
      setError(formatError(err))
    } finally {
      setCreating(false)
    }
  }

  async function onRevoke(row: CommandCenterInvite) {
    setError(null)
    setRowBusy(row.id)
    try {
      const result = await api.commandCenter.invites.revoke({
        scope: row.scope,
        invite_id: row.id,
      })
      setInvites((prev) => prev.map((r) => (r.id === row.id ? result.invite : r)))
    } catch (err) {
      if (handleGuarded(err)) return
      setError(formatError(err))
    } finally {
      setRowBusy(null)
    }
  }

  async function onExpire(row: CommandCenterInvite) {
    setError(null)
    setRowBusy(row.id)
    try {
      const result = await api.commandCenter.invites.expire({
        scope: row.scope,
        invite_id: row.id,
      })
      setInvites((prev) => prev.map((r) => (r.id === row.id ? result.invite : r)))
    } catch (err) {
      if (handleGuarded(err)) return
      setError(formatError(err))
    } finally {
      setRowBusy(null)
    }
  }

  const header = (
    <RouteHeader
      kicker="ONBOARDING · SECURE INVITES"
      title="Secure Invites"
      description="Issue scoped, expiring invitations against the real invite tables. Operator-gated."
      crumbs={[
        { label: 'Command Center', href: '/command-center/overview' },
        { label: 'Secure Invites' },
      ]}
    />
  )

  // Forbidden and auth-required are terminal states for this admin-gated
  // surface — render the honest EmptyState and nothing else.
  if (authRequired) {
    return (
      <div>
        {header}
        <EmptyState
          title="Sign in to continue"
          body="Secure Invites is an authenticated operator surface. Sign in at /auth/login to issue and manage invitations."
        />
      </div>
    )
  }

  if (forbidden) {
    return (
      <div>
        {header}
        <EmptyState
          title="Operator access required"
          body="This is an admin-gated surface. Your session is authenticated but lacks the Operator (Master Architect) role required to issue invitations."
        />
      </div>
    )
  }

  const isRowActionable = (status: string) =>
    status !== 'revoked' && status !== 'expired'

  return (
    <div>
      {header}

      <div style={{ marginBottom: 16 }}>
        <Banner severity="info" title="Session-scoped list">
          A global invite directory lands in a later phase; per-project invites appear on each
          project. This session shows invites you created here.
        </Banner>
      </div>

      {error && (
        <div style={{ marginBottom: 16 }}>
          <Banner severity="warning" title="Invites">
            {error}
          </Banner>
        </div>
      )}

      {/* ── Create form ─────────────────────────────────────── */}
      <form
        onSubmit={onCreate}
        style={{
          ...surfaceCard,
          marginBottom: 22,
        }}
      >
        <div style={{ ...text.kicker, marginBottom: 14 }}>Issue an invite</div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14,
          }}
        >
          <FormField label="Scope" htmlFor="inv-scope">
            <Select
              id="inv-scope"
              value={scope}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setScope(e.target.value as Scope)
              }
              options={SCOPE_OPTIONS}
            />
          </FormField>

          <FormField label="Recipient email" htmlFor="inv-email" required>
            <Input
              id="inv-email"
              type="email"
              placeholder="person@example.com"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            />
          </FormField>

          {scope === 'project' ? (
            <FormField
              label="Project ID"
              htmlFor="inv-project"
              required
              help="The sq_projects id this invite grants access to."
            >
              <Input
                id="inv-project"
                placeholder="project uuid"
                value={projectId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setProjectId(e.target.value)
                }
              />
            </FormField>
          ) : (
            <FormField
              label="Org ID"
              htmlFor="inv-org"
              required
              help="The organization id this invite grants membership to."
            >
              <Input
                id="inv-org"
                placeholder="org uuid"
                value={orgId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrgId(e.target.value)}
              />
            </FormField>
          )}

          <FormField label="Role" htmlFor="inv-role" help="Optional — defaults server-side.">
            <Input
              id="inv-role"
              placeholder="e.g. client_owner"
              value={role}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRole(e.target.value)}
            />
          </FormField>

          <FormField label="Expires in" htmlFor="inv-expires">
            <Select
              id="inv-expires"
              value={expiresIn}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setExpiresIn(e.target.value as ExpiresIn)
              }
              options={EXPIRES_OPTIONS}
            />
          </FormField>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button type="submit" variant="primary" loading={creating}>
            Issue invite
          </Button>
        </div>
      </form>

      {/* ── In-session list ─────────────────────────────────── */}
      <div style={{ ...text.kicker, marginBottom: 10 }}>Issued this session</div>

      {invites.length === 0 ? (
        <EmptyState
          title="No invites issued yet"
          body="Invites you create above appear here for the rest of this session so you can revoke or expire them. Nothing is fetched from a directory — there isn't one yet."
        />
      ) : (
        <DataGrid
          columns={[
            { id: 'email', header: 'RECIPIENT' },
            {
              id: 'scope',
              header: 'SCOPE',
              width: 110,
              render: (r: CommandCenterInvite) => (
                <span style={chipStyle(SHELL_TOKENS.purple)}>{r.scope}</span>
              ),
            },
            {
              id: 'role',
              header: 'ROLE',
              mono: true,
              width: 150,
              render: (r: CommandCenterInvite) => r.role || '—',
            },
            {
              id: 'status',
              header: 'STATUS',
              width: 120,
              render: (r: CommandCenterInvite) => (
                <span style={chipStyle(statusAccent(r.status))}>{r.status}</span>
              ),
            },
            {
              id: 'expires_at',
              header: 'EXPIRES',
              mono: true,
              width: 120,
              render: (r: CommandCenterInvite) => formatStamp(r.expires_at),
            },
            {
              id: 'actions',
              header: '',
              width: 170,
              render: (r: CommandCenterInvite) => {
                const busy = rowBusy === r.id
                const actionable = isRowActionable(r.status)
                return (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    {actionable ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={busy}
                          onClick={() => onExpire(r)}
                        >
                          expire
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          loading={busy}
                          onClick={() => onRevoke(r)}
                        >
                          revoke
                        </Button>
                      </>
                    ) : (
                      <InlineAlert severity="info">closed</InlineAlert>
                    )}
                  </div>
                )
              },
            },
          ]}
          rows={invites}
          getRowId={(r: CommandCenterInvite) => r.id}
          emptyMessage="No invites issued this session."
        />
      )}
    </div>
  )
}

const surfaceCard: React.CSSProperties = {
  background: SHELL_TOKENS.surface,
  border: `1px solid ${SHELL_TOKENS.border}`,
  borderRadius: 14,
  padding: 20,
}
